import { useState } from "react";
import { SpecialtyDto, specialtyFormData, specialtyKeyId } from "../types/specialty";
import { specialtyService } from "../services/specialtyService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useSpecialties = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi και εκμεταλλευόμαστε την cache (κλειδί "specialties")
  const {
    data: specialties = [],
    loading: isFetching,
    error: getError,
    refetch: fetchSpecialties,
    invalidateCache,
  } = useApi<SpecialtyDto[]>("specialties", (signal) => specialtyService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation με τον σωστό τύπο δεδομένων φόρμας
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newSpecialty: typeof specialtyFormData) => specialtyService.create(newSpecialty));

  // 3. Το UPDATE τυλίγεται στο useMutation
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: SpecialtyDto }) =>
    specialtyService.update(id, data)
  );

  // 4. Το DELETE τυλίγεται επίσης στο useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => specialtyService.delete(id)));
    return ids;
  });

  // State αποκλειστικά για το ErrorSuccessModal
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createSpecialty = async (newSpecialty: typeof specialtyFormData): Promise<void> => {
    try {
      await createExecution(newSpecialty);
      
      // Καθαρίζουμε την cache για να ξανατραβήξει φρέσκα δεδομένα στην επόμενη προβολή
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ειδικότητα αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchSpecialties(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateSpecialties = async (id: number | string, updatedData: SpecialtyDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchSpecialties(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteSpecialties = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      // Καθαρίζουμε την cache
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchSpecialties(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    specialties,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchSpecialties: () => fetchSpecialties(true),
    createSpecialty,
    updateSpecialties,
    deleteSpecialties,
    modalState,
    closeModal,
  };
};